import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Bell, FilePlus2, DollarSign, UserPlus } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAdminContext } from '../../context/AdminContext';
import { useAuth } from '../../context/AuthContext.js';
import useNotifications from '../../hooks/useNotifications';
import '../../styles/Notifications.css';

// Función para obtener el ícono (la misma que en AdminNotifications.js)
const getNotificationIcon = (type) => {
    switch (type) {
        case 'new_agreement':
            return <FilePlus2 size={20} />;
        case 'new_withdrawal_request':
            return <DollarSign size={20} />;
        case 'new_small_owner_request':
            return <UserPlus size={20} />;
        default:
            return <Bell size={20} />;
    }
};

const NotificationBell = () => {
    const { currentUser, userProfile, adminUnreadCount } = useAdminContext();
    const { isAdmin: authIsAdmin, isSupport, hasPermission } = useAuth();
    const canViewAdminNotifications = Boolean(
        authIsAdmin || isSupport || hasPermission('panel:notificaciones')
    );
    
    // Usamos el hook de notificaciones para obtener los datos en tiempo real
    const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications(
        currentUser?.uid,
        canViewAdminNotifications,
        { soundsEnabled: userProfile?.notificationSoundsEnabled !== false }
    );

    const [isOpen, setIsOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const navigate = useNavigate();
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    // Cierra el dropdown si se hace clic fuera de él
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) && buttonRef.current && !buttonRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Recalculate dropdown position when opened, window resized or scrolled
    useEffect(() => {
        if (!isOpen) return undefined;

        const calcPos = () => {
            try {
                const btn = buttonRef.current;
                const dd = dropdownRef.current;
                if (!btn || !dd) return;
                const btnRect = btn.getBoundingClientRect();
                const ddRect = dd.getBoundingClientRect();
                const vw = window.innerWidth || document.documentElement.clientWidth;
                const isMobile = vw <= 768;

                if (isMobile) {
                    // mobile: use fixed centered top (CSS fallback handles most) — keep default
                    setDropdownStyle({ position: 'fixed', top: '60px', left: '50%', transform: 'translateX(-50%)', width: '92vw', maxWidth: '420px' });
                    return;
                }

                // Preferred: align dropdown's right edge with button's right edge if it fits
                const margin = 8; // small margin from viewport edges
                const preferredLeft = btnRect.right - ddRect.width;
                let left = preferredLeft;
                if (preferredLeft < margin) left = Math.max(margin, btnRect.left);
                if (left + ddRect.width > vw - margin) left = Math.max(margin, vw - ddRect.width - margin);

                const top = btnRect.bottom + 8; // 8px gap

                setDropdownStyle({ position: 'fixed', top: `${Math.round(top)}px`, left: `${Math.round(left)}px`, transform: 'none' });
            } catch (e) {
                // fail silently
            }
        };

        // initial calc after render
        requestAnimationFrame(calcPos);

        window.addEventListener('resize', calcPos);
        window.addEventListener('scroll', calcPos, true);
        return () => {
            window.removeEventListener('resize', calcPos);
            window.removeEventListener('scroll', calcPos, true);
        };
    }, [isOpen]);

    const handleNotificationClick = (notification) => {
        if (!notification.read) {
            markAsRead(notification.id);
        }
        if (notification.link) {
            navigate(`/${notification.link}`);
        }
        setIsOpen(false); // Cierra el dropdown después de hacer clic
    };

    const handleMarkAllAsRead = () => {
        markAllAsRead();
    };

    const recentNotifications = notifications.slice(0, 5); // Mostrar solo las 5 más recientes

    const normalizeCount = (value) => (Number.isFinite(value) && value > 0 ? value : 0);
    const userUnread = normalizeCount(unreadCount);
    const adminUnread = normalizeCount(adminUnreadCount);
    const fallbackUnread = notifications.reduce((total, notif) => total + (!notif.read ? 1 : 0), 0);
    const badgeCount = canViewAdminNotifications
        ? Math.max(adminUnread, userUnread, fallbackUnread)
        : Math.max(userUnread, fallbackUnread);
    const displayCount = badgeCount > 99 ? '99+' : badgeCount;

    const toggleOpen = () => setIsOpen(v => !v);

    // Indica si hay al menos una notificación no leída (booleano)
    const hasUnread = (notifications && notifications.length > 0 && notifications.some(n => !n.read)) || userUnread > 0 || adminUnread > 0;

    return (
        <div className="notification-icon" ref={dropdownRef}>
            <div className="notification-bell-wrapper">
                <button ref={buttonRef} className="notification-button" onClick={toggleOpen} aria-haspopup="true" aria-expanded={isOpen} aria-label={hasUnread ? `Tienes ${displayCount} notificaciones` : 'Notificaciones'}>
                    <MessageSquare size={22} aria-hidden="true" />
                </button>
                {badgeCount > 0
                    ? <span className="notification-badge">{displayCount}</span>
                    : (hasUnread ? <span className="notification-dot-green" /> : null)
                }
            </div>
            {isOpen && (
                <div ref={dropdownRef} className="notification-dropdown" style={dropdownStyle}>
                    <div className="notification-header">
                        Notificaciones
                        {unreadCount > 0 && (
                            <button onClick={handleMarkAllAsRead} className="mark-all-read-btn">
                                Marcar todas como leídas
                            </button>
                        )}
                    </div>
                    <div className="notification-list">
                        {recentNotifications.length > 0 ? (
                            recentNotifications.map(n => (
                                <div key={n.id} className={`notification-item ${!n.read ? 'unread' : ''}`} onClick={() => handleNotificationClick(n)}>
                                    {getNotificationIcon(n.type)}
                                    <div className="notification-content">
                                        <p>{n.message}</p>
                                        <small>{n.createdAt?.toDate() ? formatDistanceToNow(n.createdAt.toDate(), { addSuffix: true, locale: es }) : ''}</small>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="notification-empty">No hay notificaciones nuevas.</div>
                        )}
                    </div>
                    <div className="notification-footer">
                        <button onClick={() => { navigate('/notificaciones'); setIsOpen(false); }}>
                            Ver todas las notificaciones
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;